import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import ReactPixel from "react-facebook-pixel";
import Typography from "@mui/joy/Typography";
import Link from "@mui/joy/Link";
import { theme } from "../../styles/theme";
import { useMutation, useQuery } from "@tanstack/react-query";
import { paymentApi } from "../../api/paymentApi";
import { CheckLottie } from "../../components/common/CheckLottie";
import EmailSubmit from "./EmailSubmit";
import { BottomNextButtonPromotion } from "../../components/common/BottomNextButtonPromotion";
import { gtagConversion } from "../../utils/gtag";

export default function TossPaymentsPromotionSuccess() {
  const [searchParams] = useSearchParams();

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [text, setText] = useState<string>("");
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);

  const isButtonDisabled = loading || text.trim() === "" || !isEmailValid;
  const [isEmailSubmitted, setEmailSubmitted] = useState(false);

  const orderId = searchParams.get("orderId");
  const paymentKey = searchParams.get("paymentKey");
  const payAmount = Number(searchParams.get("amount"));

  const { isLoading, isSuccess, isError } = useQuery({
    queryKey: ["tossPromoConfirm", orderId, paymentKey, payAmount],
    enabled: Boolean(orderId && paymentKey && payAmount),
    queryFn: async () => {
      return await paymentApi.confirmTossPaymentsPromotion({
        orderId: orderId as string,
        paymentKey: paymentKey as string,
        payAmount: payAmount as number,
      });
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 0,
    onSuccess: (responseData) => {
      if (
        responseData?.result?.isConfirmed !== true &&
        responseData?.result?.failureMessage
      ) {
        alert(responseData?.result?.failureMessage);
        return;
      }
      ReactPixel.track("Purchase", {
        value: payAmount,
        currency: "KRW",
      });

      gtagConversion("6CytCP-f0Z8bEM2r2Lso", {
        value: payAmount,
        currency: "KRW",
        transaction_id: orderId || undefined,
      });
    },
    onError: () => {
      alert("결제가 실패했어요. 다시 시도해 주세요.");
    },
  });

  const { mutate: submitEmail } = useMutation({
    mutationFn: (vars: {
      orderId: string;
      paymentKey: string;
      email: string;
    }) =>
      paymentApi.submitEmailTossPaymentsPromotion({
        orderId: vars.orderId,
        paymentKey: vars.paymentKey,
        email: vars.email,
      }),
    onSuccess: () => {
      setEmailSubmitted(true);
      setLoading(false);
    },
  });

  const handleEmailSubmit = (email: string) => {
    if (!isEmailValid) return;
    if (!orderId || !paymentKey) return;

    setLoading(true);

    submitEmail({ orderId, paymentKey, email });
  };

  if (!orderId || !paymentKey || !payAmount) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <Typography variant={"H3"}>{"만료된 페이지"}</Typography>
          </Stack>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  if (isError) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <Typography variant={"H3"}>
              {"결제 처리 중 오류가 발생했습니다."}
            </Typography>
            <Typography variant={"H3"}>
              <Link
                href="mailto:support@orwellhealth.org"
                sx={{
                  textDecoration: "underline",
                  color: theme.vars.palette.Content.Weak,
                }}
              >
                support@orwellhealth.org
              </Link>
              {"로 문의해 주세요."}
            </Typography>
          </Stack>
        }
      />
    );
  }

  if (isSuccess) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center" spacing={2}>
            <CheckLottie containerSize={"72px"} />
            <Typography variant={"H3"}>
              {!isEmailSubmitted
                ? "결제가 완료되었습니다!"
                : "이메일 제출이 완료되었습니다!"}
            </Typography>
            {!isEmailSubmitted ? (
              <Stack alignItems={"center"} spacing={0.5}>
                <Stack direction={"row"} spacing={"0.1px"}>
                  <Typography
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.HighlightWithAlert,
                      fontWeight: 500,
                      px: 0,
                    }}
                  >
                    {"(필수)"}
                  </Typography>
                  <Typography
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 500,
                    }}
                  >
                    {"프로그램 이용방법을 안내받을"}
                  </Typography>
                </Stack>
                <Typography
                  variant={"Subtitle"}
                  sx={{
                    color: theme.vars.palette.Content.HighlightWithAlert,
                    fontWeight: 500,
                  }}
                >
                  {"이메일 주소를 입력해 주세요."}
                </Typography>
              </Stack>
            ) : (
              <Stack alignItems={"center"} spacing={0.5}>
                <Typography
                  variant={"Body"}
                  sx={{
                    color: theme.vars.palette.Content.Strong,
                    fontWeight: 500,
                  }}
                >
                  {"혹시 메일을 받지 못하거나 문제가 발생한다면"}
                </Typography>
                <Typography
                  variant={"Body"}
                  sx={{
                    color: theme.vars.palette.Content.Strong,
                    fontWeight: 500,
                  }}
                >
                  <Link
                    href="mailto:support@orwellhealth.org"
                    sx={{
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 500,
                    }}
                  >
                    support@orwellhealth.org
                  </Link>
                  {"로 문의해 주세요."}
                </Typography>
              </Stack>
            )}

            {!isEmailSubmitted && (
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={1}
                sx={{ width: "100%" }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "640px",
                    height: "54px",
                    backgroundColor: "transparent",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Weak}`,
                  }}
                >
                  <EmailSubmit
                    initialValue={text}
                    handleChange={(newValue: string) => setText(newValue)}
                    onValidityChange={(valid: boolean) =>
                      setIsEmailValid(valid)
                    }
                    isFocused={isFocused}
                    setIsFocused={setIsFocused}
                    placeholder={"이메일을 입력하세요"}
                  />
                </Box>
              </Stack>
            )}

            {!isEmailSubmitted && (
              <BottomNextButtonPromotion
                loading={loading}
                disabled={isButtonDisabled}
                onClick={() => handleEmailSubmit(text!!)}
                text={"다음"}
                isInputFocused={isFocused}
              />
            )}
          </Stack>
        }
      />
    );
  }

  return null;
}
